<template>
  <div>
    <footer class="srs-footer bg-[#FFF] border-t border-[#D1D1D1] overflow-hidden">
    <!-- Main Footer Content -->
    <div class="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-8">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-10">
        <!-- Logo & Social Media -->
        <div class="lg:col-span-2 flex flex-col items-start gap-4">
          <a :href="`${srsUrl}/${locale}/`" target="_blank" rel="noopener noreferrer">
            <img 
              class="w-24 md:w-28 h-auto max-w-full" 
              :src="`${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_LANDING_PATH}LogoFooter.png`" 
              alt="SRS Distribution" 
            />
          </a>
          <div class="flex gap-3 flex-wrap">
            <a
              href="https://www.facebook.com/SRSDistributionInc/"
              title="SRS Distribution Inc. on Facebook"
              target="_blank"
              class="social-link"
              ><i class="ri-facebook-fill text-xl"></i
            ></a>
            <a
              href="https://www.instagram.com/srs.distribution.inc/?hl=en"
              title="SRS Distribution Inc. on Instagram"
              target="_blank"
              class="social-link"
              ><i class="ri-instagram-fill text-xl"></i
            ></a>
            <a
              href="https://twitter.com/SRSdistribution"
              title="SRS Distribution Inc. on Twitter"
              target="_blank"
              class="social-link"
              ><i class="ri-twitter-x-fill text-xl"></i
            ></a>
            <a
              href="https://www.youtube.com/channel/UCgKlk2s8yGYH-Y4ZIX76rTg"
              title="SRS Distribution Inc. on YouTube"
              target="_blank"
              class="social-link"
              ><i class="ri-youtube-fill text-xl"></i
            ></a>
            <a
              href="https://www.linkedin.com/company/756621"
              title="SRS Distribution Inc. on LinkedIn"
              target="_blank"
              class="social-link"
              ><i class="ri-linkedin-fill text-xl"></i
            ></a>
          </div>
        </div>

        <!-- Address & Contact Info -->
        <div class="lg:col-span-3 flex flex-col gap-2 text-sm font-normal leading-[130%] text-[#6E6E6E] text-left">
          <div>
            <a
              href="https://www.google.com/maps/place/SRS+Distribution/33.1351165,-96.7097013,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x2f1e36fdce08e979!8m2!3d33.1351165!4d-96.7075127"
              target="_blank"
              class="footer-link block"
            >
              7440 State Highway 121<br />
              McKinney, TX 75070-2196
            </a>
          </div>
          <div class="flex flex-col gap-1.5">
            <div class="text-[#6E6E6E]">
              {{ t('footer.phone') }}: <a
                :title="t('footer.phone')"
                class="footer-link"
                href="tel:2144914149"
              >214.491.4149</a>
            </div>
            <div class="text-[#6E6E6E]">
              {{ t('footer.fax') }}: 214.491.4156
            </div>
            <div class="text-[#6E6E6E] break-words">
              {{ t('footer.email') }}: <a
                href="mailto:info@srsdistribution.com"
                class="footer-link"
              >info@srsdistribution.com</a>
            </div>
          </div>
        </div>

        <!-- Navigation Links - 2 columns -->
        <div class="lg:col-span-4 grid grid-cols-2 gap-x-6 gap-y-3 text-left">
          <div class="flex flex-col gap-3 text-sm font-normal leading-[150%]">
            <a
              :href="`${srsUrl}/${locale}/about/`"
              :title="t('footer.about')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.about') }}</a>
            <a
              :href="`${srsUrl}/${locale}/markets/find-a-branch/`"
              :title="t('footer.locations')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.locations') }}</a>
            <a
              :href="`${srsUrl}/${locale}/contact/`"
              :title="t('footer.contact')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.contact') }}</a>
          </div>
          <div class="flex flex-col gap-3 text-sm font-normal leading-[150%]">
            <a
              :href="`${srsUrl}/${locale}/careers/`"
              :title="t('footer.careers')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.careers') }}</a>
            <a
              :href="`${srsUrl}/${locale}/news-and-events/events`"
              :title="t('footer.events')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.events') }}</a>
            <a
              :href="`${srsUrl}/${locale}/news-and-events/news/`"
              :title="t('footer.news')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.news') }}</a>
          </div>
        </div>

        <!-- App Download Section -->
        <div class="lg:col-span-3 flex flex-col items-start w-full">
          <div class="bg-[#F4EAEA] rounded-lg p-6 w-full max-w-[314px] flex flex-col gap-2">
            <h3 class="text-sm font-bold leading-[150%] text-[#545454]">
              {{ t('footer.downloadApp') }}
            </h3>
            <div class="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <a
                href="https://apps.apple.com/us/app/roof-hub/id1434560046"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-block"
              >
                <img
                  :src="appleStoreImg"
                  alt="Download on the App Store"
                  class="h-[39px] w-auto max-w-full"
                />
              </a>
              <a
                :href="`https://play.google.com/store/apps/details?id=roofhub.pro&hl=${locale}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-block"
              >
                <img
                  :src="googlePlayImg"
                  alt="Get it on Google Play"
                  class="h-[39px] w-auto max-w-full"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Footer - Legal Links -->
    <div class="border-t border-[#D1D1D1] bg-[#FFF]">
      <div class="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 text-xs font-normal leading-[140%]">
          <!-- Copyright - Left on desktop -->
          <div class="text-[#6E6E6E] order-2 md:order-1">
            &copy; {{ currentYear }} {{ t('footer.copyright') }}
          </div>

          <!-- All Legal Links & Language - Right on desktop -->
          <div class="order-1 md:order-2 flex flex-wrap items-center gap-x-4 md:gap-x-6 gap-y-2">
            <a
              :href="termsUrl"
              :title="t('footer.terms')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.terms') }}</a>
            <a
              :href="privacyPolicyUrl"
              :title="t('footer.privacy')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.privacy') }}</a>
            <a
              :href="termsOfSaleUrl"
              :title="t('footer.privacy')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.termsofsale') }}</a>
            <a
              href="javascript:void(0)"
              class="footer-link"
            >{{ t('footer.cookiePreferences') }}</a>
            <a
              :href="rightsRequestUrl"
              :title="t('footer.donotsell')"
              target="_blank"
              class="footer-link"
            >{{ t('footer.donotsell') }}</a>
            <a 
              href="javascript:void(0)"
              @click="openPrivacyChoices"
              class="footer-link"
            >{{ t('privacy.yourChoices') }}</a>
            <a 
              href="javascript:void(0)"
              @click="changeLang()"
              class="flex items-center gap-1.5 text-base font-medium leading-[120%] text-[#6E6E6E] hover:text-red-700 transition-colors"
            >
              <img
                :src="`${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_LANDING_PATH}EarthLine.png`"
                :alt="t('footer.language')"
                class="w-4 h-4 flex-shrink-0"
              />
              {{ locale === "en" ? t('footer.spanish') : t('footer.english') }}
            </a>
          </div>
        </div>
      </div>
    </div>
    </footer>
    <DataCompliance />
  </div>
</template>

<script setup>
import { MEDIA_ASSET_BASE_URL, MEDIA_ASSET_LANDING_PATH } from "../config/constants.js";
import { computed } from "vue";
import { setCookie } from '@/utilities/cookies';
import DataCompliance from './DataCompliance.vue';
const srsUrl = import.meta.env.VITE_SRS_URL;

import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

    function getStoreImage(name) {
        return computed(() =>
            `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_LANDING_PATH}${name}${locale.value === "es" ? "ES" : ""}.png`
        );
    }

    const appleStoreImg = getStoreImage("AppleStore");
    const googlePlayImg = getStoreImage("GooglePlay");

const changeLang = () => {
  const lang = locale.value === "en" ? "es" : "en";
  locale.value = lang;
  setCookie("epslanguage", lang);
}

const currentYear = (new Date()).getFullYear();

const termsUrl = computed(() => {
  return (window.appConfig?.rhVrLegalNoticeAndTerms && window.appConfig?.customerCode) ? '/terms-of-use' : '/terms';
});

const rightsRequestUrl = computed(() => {
  return (window.appConfig?.rhVrLegalNoticeAndTerms && window.appConfig?.customerCode) ? '/privacy-rights-request-page' : '/privacy-rights-request';
});

const privacyPolicyUrl = computed(() => {
  return (window.appConfig?.rhVrLegalNoticeAndTerms && window.appConfig?.customerCode) ? '/privacypolicy' : '/privacy';
});

const termsOfSaleUrl = computed(() => {
  return (window.appConfig?.rhVrLegalNoticeAndTerms && window.appConfig?.customerCode) ? '/termsofsale' : '/terms-of-sale';
});

function openPrivacyChoices() {
  if (typeof window.openPrivacyPreferences === 'function') {
    window.openPrivacyPreferences();
  }
}

</script>

<style scoped>
/* Footer link styles */
.footer-link {
  @apply text-[#6E6E6E] hover:text-red-700 hover:underline transition-colors;
}

.social-link {
  @apply text-gray-600 hover:text-red-700 transition-colors;
}
</style>